/**
 * Từ vựng nghiệp vụ SCR-036/SCR-037 (S11.6): nhãn + sắc thái cho loại mộ, trạng thái
 * vị trí, trạng thái danh tính và tổng hợp liên kết mộ–liệt sĩ. UI presentation-only —
 * mã lạ LUÔN hiển thị nguyên văn, tone neutral.
 *
 * `physicalCondition` KHÔNG có bảng nhãn ở đây: catalog tình trạng vật lý do domain
 * owner khóa riêng (CR-S11-3-001 D1), UI không tự phát minh danh mục — giá trị hiển
 * thị nguyên văn như API trả về.
 */
import type { GraveListMartyrSummary, GravePositionState, GraveTypeCode } from "./graveApi";
import { label } from "../../lib/labels";

export type GraveTone = "info" | "success" | "warning" | "neutral";

/**
 * Mã loại mộ ĐÓNG theo contract — chỉ dùng để kiểm tra giá trị filter trước khi gửi
 * transport (`Record<string, …>` bên dưới vẫn nhận mã lạ để hiển thị nguyên văn).
 */
export const GRAVE_TYPE_CODES = ["INDIVIDUAL", "COLLECTIVE", "UNKNOWN"] as const satisfies
  ReadonlyArray<GraveTypeCode>;

/** Trả mã loại mộ hợp lệ, hoặc `undefined` khi giá trị không thuộc enum contract. */
export function toGraveTypeCode(value: string): GraveTypeCode | undefined {
  return GRAVE_TYPE_CODES.find((code) => code === value);
}

/** Mã trạng thái vị trí ĐÓNG theo contract — dùng cho filter transport. */
export const POSITION_STATE_CODES = ["AVAILABLE", "OCCUPIED", "CLOSED"] as const satisfies
  ReadonlyArray<GravePositionState>;

/** Trả mã trạng thái vị trí hợp lệ, hoặc `undefined` khi ngoài enum contract. */
export function toPositionStateCode(value: string): GravePositionState | undefined {
  return POSITION_STATE_CODES.find((code) => code === value);
}

export const GRAVE_TYPE_LABELS: Readonly<
  Record<string, Readonly<{ tone: GraveTone }>>
> = {
  INDIVIDUAL: { tone: "info" },
  COLLECTIVE: { tone: "info" },
  UNKNOWN: { tone: "neutral" },
};

export function graveTypeLabel(code: string, displayName?: unknown): string {
  return label(code, displayName);
}

export function graveTypeTone(code: string): GraveTone {
  return GRAVE_TYPE_LABELS[code]?.tone ?? "neutral";
}

/**
 * Nhãn và sắc thái cho loại liên kết mộ–liệt sĩ (CONFIRMED, SUSPECTED, COLLECTIVE).
 */
export const LINK_TYPE_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: GraveTone }>>
> = {
  CONFIRMED: { label: "Đã xác nhận", tone: "success" },
  SUSPECTED: { label: "Nghi vấn", tone: "warning" },
  COLLECTIVE: { label: "Tập thể", tone: "info" },
};

export function linkTypeLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return LINK_TYPE_LABELS[code]?.label ?? label(code, displayName);
}

export function linkTypeTone(code: string): GraveTone {
  return LINK_TYPE_LABELS[code]?.tone ?? "neutral";
}

/**
 * Nhãn độ tin cậy của liên kết mộ–liệt sĩ (HIGH, MEDIUM, LOW).
 */
export const CONFIDENCE_STATUS_LABELS: Readonly<Record<string, string>> = {
  HIGH: "Cao",
  MEDIUM: "Trung bình",
  LOW: "Thấp",
};

export function confidenceStatusLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return CONFIDENCE_STATUS_LABELS[code] ?? label(code, displayName);
}

export const POSITION_STATE_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: GraveTone }>>
> = {
  AVAILABLE: { label: "Còn trống", tone: "neutral" },
  OCCUPIED: { label: "Đang an táng", tone: "success" },
  CLOSED: { label: "Đã đóng", tone: "warning" },
};

export function positionStateLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return POSITION_STATE_LABELS[code]?.label ?? label(code, displayName);
}

export function positionStateTone(code: string): GraveTone {
  return POSITION_STATE_LABELS[code]?.tone ?? "neutral";
}

/**
 * Trạng thái danh tính của mộ.
 * Nhãn hiển thị được cung cấp bởi API displayName, FE lưu mapping dự phòng và tone.
 */
export const IDENTITY_STATE_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: GraveTone }>>
> = {
  KNOWN: { label: "Đã xác định", tone: "success" },
  UNKNOWN: { label: "Chưa xác định", tone: "neutral" },
  PARTIAL: { label: "Một phần", tone: "warning" },
};

export function identityStateLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return IDENTITY_STATE_LABELS[code]?.label ?? label(code, displayName);
}

export function identityStateTone(code: string): GraveTone {
  return IDENTITY_STATE_LABELS[code]?.tone ?? "neutral";
}

/** Nhãn cho dòng "Liệt sĩ an táng" khi KHÔNG có tên cụ thể (đã mask hoặc chưa có link). */
export const MARTYR_LINK_DISPLAY: Readonly<
  Record<string, Readonly<{ label: string; tone: GraveTone }>>
> = {
  SINGLE: { label: "Một liệt sĩ", tone: "info" },
  MULTIPLE: { label: "Nhiều liệt sĩ", tone: "info" },
  COLLECTIVE: { label: "Mộ tập thể", tone: "info" },
  NONE: { label: "Chưa có thông tin", tone: "neutral" },
};

export function martyrLinkTone(mode: string): GraveTone {
  return MARTYR_LINK_DISPLAY[mode]?.tone ?? "neutral";
}

/**
 * Nhãn "Liệt sĩ an táng" cho một dòng danh sách mộ: `SINGLE` có tên thì hiển thị tên
 * nghiệp vụ; các chế độ còn lại (và dòng thiếu `martyrLinkSummary`) dùng nhãn trung
 * thực — không suy diễn danh tính người an táng.
 */
export function martyrLinkLabel(summary: GraveListMartyrSummary | null | undefined): string {
  if (!summary) return "";
  const name = summary.displayName?.trim();
  if (summary.displayMode === "SINGLE" && name !== undefined && name !== "") return name;
  const display = (summary as { displayModeDisplayName?: string }).displayModeDisplayName;
  if (typeof display === "string" && display.trim().length > 0) {
    return display.trim();
  }
  return MARTYR_LINK_DISPLAY[summary.displayMode]?.label ?? label(summary.displayMode, display);
}

/** Dòng `SINGLE` có tên thật hay chỉ là nhãn chế độ (để chọn cách render). */
export function martyrLinkHasName(
  summary: GraveListMartyrSummary | null | undefined,
): boolean {
  if (!summary || summary.displayMode !== "SINGLE") return false;
  const name = summary.displayName?.trim();
  return name !== undefined && name !== "";
}
